import {
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import Paper from "@mui/material/Paper";
import html2pdf from "html2pdf.js";
import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { DateRangePicker } from "react-date-range";
import { useHistory } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import Layout from "../../components/layout";
import bookingService from "../../services/booking";
import DatePickerModal from "../DatePickerModal";
import RejectionModal from "../RejectionModal";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  DecryptData,
  capitalizeEachWord,
  jsonParser,
} from "../../helpers/helper";
import PrintIcon from "@mui/icons-material/Print";
import { printStaffReceipts } from "../../components/print";

const columns = [
  { id: "sr_no", label: "Sr no.", minWidth: 50, sortable: false },
  { id: "id", label: "BookingId", minWidth: 50, sortable: false },

  {
    id: "name",
    label: "Name",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "paymentMethod",
    label: "Payment Method",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "grandTotal",
    label: "Total Amount",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "amountPaid",
    label: "Amount Paid",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "discount",
    label: "Discount",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "pending",
    label: "Pending",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "bookingDate",
    label: "Date",
    minWidth: 100,
    sortable: true,
  },

  {
    id: "details",
    label: "Details",
    minWidth: 100,
    sortable: false,
  },
];

export default function BookingList() {
  const history = useHistory();
  const toggleContainer = useRef<HTMLDivElement | null>(null);

  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortBy, setSortBy] = useState<string>("");
  const [tableData, setTableData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpenDate, setIsModalOpenDate] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");

  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  // pagination
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = React.useState(0);
  const currentPage = 1;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const range = [];
  const minRange = Math.max(1, currentPage - 2);
  const maxRange = Math.min(totalPages, currentPage + 2);
  for (let i = minRange; i <= maxRange; i++) {
    range.push(i);
  }
  const indexOfLastItem = (page + 1) * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const applyFilters = (
    data,
    searchValue = searchTerm,
    paymentValue = paymentMethod
  ) => {
    let filtered = [...data];

    // Name / Email / Phone filter
    if (searchValue.trim() !== "") {
      filtered = filtered.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.email?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.phone_no?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Payment Method filter
    if (paymentValue.trim() !== "") {
      filtered = filtered.filter((item) => {
        const methods = (item.paymentMethods || "")
          .split(",")
          .map((m) => m.trim().toLowerCase());

        if (paymentValue.toLowerCase() === "both") {
          return (
            methods.includes("cash") &&
            methods.includes("online") &&
            methods.length === 2
          );
        }

        if (paymentValue.toLowerCase() === "cash") {
          return methods.length === 1 && methods[0] === "cash";
        }

        if (paymentValue.toLowerCase() === "online") {
          return methods.length === 1 && methods[0] === "online";
        }

        return false;
      });
    }

    return filtered;
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    const filtered = applyFilters(tableData, value, paymentMethod);

    setFilteredData(filtered);
  };

  const handleChangePaymentMethod = (event) => {
    const value = event.target.value;
    setPaymentMethod(value);

    const filtered = applyFilters(tableData, searchTerm, value);

    setFilteredData(filtered);
  };

  const fetchBookingData = async () => {
    const staticStartDate = localStorage.getItem("staticStartDateBooking");
    const staticEndDate = localStorage.getItem("staticEndDateBooking");
    // Set default dates to current month if not in localStorage
    let startDate, endDate;

    if (staticStartDate && staticEndDate) {
      const startDates = JSON.parse(staticStartDate);
      const endDates = JSON.parse(staticEndDate);
      startDate = new Date(startDates);
      endDate = new Date(endDates);
    } else {
      // Default to current month
      const now = new Date();
      startDate = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month

      // Save to localStorage
      localStorage.setItem("staticStartDateBooking", JSON.stringify(startDate));
      localStorage.setItem("staticEndDateBooking", JSON.stringify(endDate));
    }

    const selectionRanges = {
      startDate: startDate, // or just startDate,
      endDate: endDate, // or just endDate,
      key: "selection",
    };

    setSelectionRange(selectionRanges);

    const isSameDate =
      startDate.getDate() === endDate.getDate() &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear();

    let formattedDate = "";

    if (isSameDate) {
      formattedDate = startDate.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } else {
      const sameMonth = startDate.getMonth() === endDate.getMonth();
      const sameYear = startDate.getFullYear() === endDate.getFullYear();

      if (sameMonth && sameYear) {
        formattedDate = `${startDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })} - ${endDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
      } else if (!sameMonth && sameYear) {
        formattedDate = `${startDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })} - ${endDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
      } else {
        formattedDate = `${startDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })} - ${endDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
      }
    }

    const formatToLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`; // Format: "YYYY-MM-DD"
    };

    setSelectedDate(formattedDate);

    const allFilters = {
      startDate: formatToLocalDate(startDate),
      endDate: formatToLocalDate(endDate),
    };

    const resultData = await bookingService.filteredData({
      allFilters,
    });
    const decryptedData = await DecryptData(resultData);
    const parsedData = jsonParser(decryptedData);
    // const paymentMethodsString = paymentMethods.join(", ");
    const bookingData = await Promise.all(
      parsedData.map(async (data) => ({
        id: data.id,
        name: DecryptData(data.name),
        comments: DecryptData(data.comments),
        bookingDate: data.bookingDate,
        grandTotal: data?.grandTotal,
        discount: data?.discount,
        tax: data?.tax,
        totalPrice: data?.totalPrice,
        pendingAmount: data?.pendingAmount,
        paymentMethods: data.paymentMethods.join(", "),
      }))
    );

    // Replace 92 with 0 safely
    const enhancedBookingData = bookingData.map((data) => ({
      ...data,
      phone_no: data?.phone_no ? data.phone_no.replace(/^92/, "0") : "",
    }));

    enhancedBookingData.sort((a, b) => {
      // Convert "18, Mar 2026 12:42" to a format JS can parse
      // We replace the comma to make it "18 Mar 2026 12:42"
      const dateA = new Date(a.bookingDate.replace(",", ""));
      const dateB = new Date(b.bookingDate.replace(",", ""));

      // For Descending order (Newest first):
      return dateB.getTime() - dateA.getTime();

      // For Ascending order (Oldest first), use:
      // return dateA.getTime() - dateB.getTime();
    });
    console.log(enhancedBookingData);
    setFilteredData(enhancedBookingData);
    setTableData(enhancedBookingData);
  };

  useEffect(() => {
    fetchBookingData();
  }, []);

  const handleSort = (columnId: string) => {
    if (columnId === "sr_no" || columnId === "details") return; // Skip sorting for these columns
    if (sortBy === columnId) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnId);
      setSortOrder("asc");
    }
  };

  const sortedItems = [...currentItems].sort((a, b) => {
    if (sortBy === "sr_no" || sortBy === "details") return 0; // Skip sorting for these columns
    // Handle date sorting
    if (sortBy === "fromDate") {
      const dateA = new Date(a.fromDate);
      const dateB = new Date(b.fromDate);

      return sortOrder === "asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }
    if (sortBy === "to_date") {
      const dateA = new Date(a.to_date);
      const dateB = new Date(b.to_date);

      return sortOrder === "asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }

    if (a[sortBy] < b[sortBy]) return sortOrder === "asc" ? -1 : 1;
    if (a[sortBy] > b[sortBy]) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleClickOutside = (event: MouseEvent) => {
    if (
      toggleContainer.current &&
      !toggleContainer.current.contains(event.target as Node)
    ) {
      setIsModalOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentId(null);
  };

  const closeModalWithDelete = async () => {
    setIsModalOpen(false);
    setCurrentId(null);
    await bookingService.delete(currentId);
    fetchBookingData();
  };

  const handleServiceView = async (id) => {
    history.push(`/booking/${id}`);
  };

  // const handleEditService = async (id) => {
  //   history.push(`/booking/edit/${id}`);
  // };

  const handleDeleteService = async (id) => {
    setIsModalOpen(true);
    setCurrentId(id);
  };

  function handleDownloadPdf() {
    const table = document.getElementById("your-table-id");

    if (table) {
      const container = document.createElement("div");
      container.style.width = "190mm";
      container.style.padding = "10mm";
      const header = document.createElement("div");
      header.innerHTML = `
        <img src="/logo.png" style="display: block; margin: 0 auto; max-width: 180mm; width: 100%; height: auto;" />
      `;
      container.appendChild(header);

      const beforeTable = document.createElement("div");
      beforeTable.innerHTML = '<p style="text-align: center;"></p>';
      container.appendChild(beforeTable);

      const clonedTable = table.cloneNode(true) as HTMLElement;

      const rows = clonedTable.querySelectorAll("tr");
      rows.forEach((row) => {
        const lastCell = row.lastElementChild;
        lastCell?.remove();
      });

      container.appendChild(clonedTable);

      const afterTable = document.createElement("div");
      afterTable.innerHTML =
        '<div style="margin-top:5%;display:flex;flex-direction:row;justify-content:space-around;border-top:1px solid black"><div style="width:30%"><p style="color:#444444">2nd Floor, Plaza 147, Central Commercial, Bahria Orchard, Lahore, 55150, Pakistan</p></div><div><p style="color:#444444">elegancemakeupstudioo@gmail.com</p></div><div><p style="color:#444444">+92 331 555 5933</p></div></div>';
      container.appendChild(afterTable);

      const footer = document.createElement("div");
      footer.innerHTML = '<p style="height:20px"><br></p>';
      container.appendChild(footer);

      // Set CSS styles to avoid content overlap
      const style = document.createElement("style");
      style.innerHTML = `
            .page-break {
              page-break-before: always;
            }
            .container {
              margin-bottom: 50px;
              width: 100%;
              box-sizing: border-box;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed; /* Prevents overflow */
            }
            td, th {
              word-wrap: break-word;
              font-size: 10px; /* Optional: reduce font size for better fit */
            }
            tr {
              page-break-inside: avoid;
            }
          `;
      container.appendChild(style);

      // Add a page break to avoid overlap
      const pageBreak = document.createElement("div");
      pageBreak.className = "page-break";
      container.appendChild(pageBreak);

      const htmlString = container.innerHTML;

      html2pdf()
        .from(htmlString)
        .set({
          margin: 10,
          filename: "user.pdf",
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .save();
    }
  }

  const closeModalDate = () => {
    setIsModalOpenDate(false);
    const sidebar = document.querySelector(".sidebarParent");
    const navbar = document.querySelector(".main__navbar__right");
    if (sidebar) {
      sidebar.classList.remove("blurred__sidebar");
    }
    if (navbar) {
      navbar.classList.remove("blurred__sidebar");
    }
  };

  const openModalDate = () => {
    setIsModalOpenDate(true);
    const sidebar = document.querySelector(".sidebarParent");
    const navbar = document.querySelector(".main__navbar__right");
    if (sidebar) {
      sidebar.classList.add("blurred__sidebar");
    }
    if (navbar) {
      navbar.classList.add("blurred__sidebar");
    }
  };

  const handleSelect = async (ranges) => {
    setSelectionRange(ranges.selection);
  };

  const staticRanges = [
    {
      label: "Today",
      range: () => ({
        startDate: new Date(),
        endDate: new Date(),
      }),
      isSelected: (range) => {
        const today = new Date();
        return (
          range.startDate.toDateString() === today.toDateString() &&
          range.endDate.toDateString() === today.toDateString()
        );
      },
      key: "today",
    },
    {
      label: "Yesterday",
      range: () => ({
        startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
        endDate: new Date(new Date().setDate(new Date().getDate() - 1)),
      }),
      isSelected: (range) => {
        const yesterday = new Date(
          new Date().setDate(new Date().getDate() - 1)
        );
        return (
          range.startDate.toDateString() === yesterday.toDateString() &&
          range.endDate.toDateString() === yesterday.toDateString()
        );
      },
      key: "yesterday",
    },
    {
      label: "This Week",
      range: () => {
        const now = new Date();
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay()); // Sunday
        const end = new Date(start);
        end.setDate(start.getDate() + 6); // Saturday
        return { startDate: start, endDate: end };
      },
      isSelected: (range) => {
        const now = new Date();
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay());
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        return (
          range.startDate.toDateString() === start.toDateString() &&
          range.endDate.toDateString() === end.toDateString()
        );
      },
      key: "thisWeek",
    },
    {
      label: "Last Week",
      range: () => ({
        startDate: new Date(
          new Date().setDate(new Date().getDate() - new Date().getDay() - 7)
        ),
        endDate: new Date(
          new Date().setDate(new Date().getDate() - new Date().getDay() - 1)
        ),
      }),
      isSelected: (range) => {
        const startOfLastWeek = new Date(
          new Date().setDate(new Date().getDate() - new Date().getDay() - 7)
        );
        return (
          range.startDate.toDateString() === startOfLastWeek.toDateString() &&
          range.endDate.toDateString() ===
            new Date(
              new Date().setDate(new Date().getDate() - new Date().getDay() - 1)
            ).toDateString()
        );
      },
      key: "lastWeek",
    },
    {
      label: "This Month",
      range: () => {
        const start = new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          1
        );
        const end = new Date(
          new Date().getFullYear(),
          new Date().getMonth() + 1,
          0
        );
        return { startDate: start, endDate: end };
      },
      isSelected: (range) => {
        const start = new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          1
        );
        const end = new Date(
          new Date().getFullYear(),
          new Date().getMonth() + 1,
          0
        );
        return (
          range.startDate.toDateString() === start.toDateString() &&
          range.endDate.toDateString() === end.toDateString()
        );
      },
      key: "thisMonth",
    },
    {
      label: "Last Month",
      range: () => ({
        startDate: new Date(
          new Date().getFullYear(),
          new Date().getMonth() - 1,
          1
        ),
        endDate: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      }),
      isSelected: (range) => {
        const startOfLastMonth = new Date(
          new Date().getFullYear(),
          new Date().getMonth() - 1,
          1
        );
        return (
          range.startDate.toDateString() === startOfLastMonth.toDateString() &&
          range.endDate.toDateString() ===
            new Date(
              new Date().getFullYear(),
              new Date().getMonth(),
              0
            ).toDateString()
        );
      },
      key: "lastMonth",
    },
    {
      label: "This Year",
      range: () => ({
        startDate: new Date(new Date().getFullYear(), 0, 1),
        endDate: new Date(new Date().getFullYear(), 11, 31),
      }),
      isSelected: (range) => {
        const startOfYear = new Date(new Date().getFullYear(), 0, 1);
        return (
          range.startDate.toDateString() === startOfYear.toDateString() &&
          range.endDate.toDateString() ===
            new Date(new Date().getFullYear(), 11, 31).toDateString()
        );
      },
      key: "thisYear",
    },
    {
      label: "Last Year",
      range: () => ({
        startDate: new Date(new Date().getFullYear() - 1, 0, 1),
        endDate: new Date(new Date().getFullYear() - 1, 11, 31),
      }),
      isSelected: (range) => {
        const startOfLastYear = new Date(new Date().getFullYear() - 1, 0, 1);
        return (
          range.startDate.toDateString() === startOfLastYear.toDateString() &&
          range.endDate.toDateString() ===
            new Date(new Date().getFullYear() - 1, 11, 31).toDateString()
        );
      },
      key: "lastYear",
    },
  ];

  const handleApply = async () => {
    const startDates = selectionRange.startDate;
    const endDates = selectionRange.endDate;
    const isSameDate =
      startDates.getDate() === endDates.getDate() &&
      startDates.getMonth() === endDates.getMonth() &&
      startDates.getFullYear() === endDates.getFullYear();

    let formattedDate = "";

    if (isSameDate) {
      formattedDate = startDates.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } else {
      const sameMonth = startDates.getMonth() === endDates.getMonth();
      const sameYear = startDates.getFullYear() === endDates.getFullYear();

      if (sameMonth && sameYear) {
        formattedDate = `${startDates.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })} - ${endDates.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
      } else if (!sameMonth && sameYear) {
        formattedDate = `${startDates.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })} - ${endDates.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
      } else {
        formattedDate = `${startDates.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })} - ${endDates.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
      }
    }

    setSelectedDate(formattedDate);

    localStorage.setItem("staticStartDateBooking", JSON.stringify(startDates));
    localStorage.setItem("staticEndDateBooking", JSON.stringify(endDates));

    const formatToLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    const allFilters = {
      startDate: formatToLocalDate(selectionRange.startDate),
      endDate: formatToLocalDate(selectionRange.endDate),
    };

    const resultData = await bookingService.filteredData({
      allFilters,
    });
    const decryptedData = await DecryptData(resultData);
    const parsedData = jsonParser(decryptedData);

    const bookingData = await Promise.all(
      parsedData.map(async (data) => ({
        id: data.id,
        name: DecryptData(data.name),
        comments: DecryptData(data.comments),
        bookingDate: data.bookingDate,
        grandTotal: data?.grandTotal,
        discount: data?.discount,
        tax: data?.tax,
        totalPrice: data?.totalPrice,
        pendingAmount: data?.pendingAmount,
        paymentMethods: data.paymentMethods.join(", "),
      }))
    );

    const enhancedBookingData = bookingData.map((data) => ({
      ...data,
      phone_no: data?.phone_no ? data.phone_no.replace(/^92/, "0") : "",
    }));

    const filtered = applyFilters(
      enhancedBookingData,
      searchTerm,
      paymentMethod
    );

    setTableData(enhancedBookingData); // keep original date result
    setFilteredData(filtered);

    closeModalDate();
  };

  const handlePrintReceipt = async (bookingId) => {
    const encrypted = await bookingService.findOne(bookingId);
    const decrypted = await DecryptData(encrypted);
    const parsed = jsonParser(decrypted)[0];

    const booking = {
      ...parsed,
      comments: parsed.comments ? await DecryptData(parsed.comments) : "",
      reason: parsed.reason ? await DecryptData(parsed.reason) : "",
      cart: parsed.services.map((item: any) => ({
        cartId: String(item.serviceId), // or item.serviceId if available
        id: Number(item.serviceId), // or item.serviceId if available
        name: item.serviceName,
        price: Number(item.servicePrice),
        qty: Number(item.quantity),
        type: item.itemType === "individual" ? "independent" : item.itemType,
      })),
      items: parsed.services.map((item: any) => ({
        ...item,
        variationText:
          item.variations.length > 0
            ? item.variations
                .map((v: any) => `${v.variationName} (${v.variationTypeName})`)
                .join(", ")
            : "",
      })),
    };

    const paymentMethodsString = booking.paymentMethods.join(", ");

    let paymentCode = "";
    const method = paymentMethodsString?.toLowerCase() || "";

    if (method === "cash") {
      paymentCode = "C";
    } else if (
      method === "online" ||
      method === "pos" ||
      method === "online,pos" ||
      method === "pos,online"
    ) {
      paymentCode = "D";
    } else {
      // If it's a mix like "cash,online" or "cash,pos"
      paymentCode = "CD";
    }

    const dateObj = new Date(booking.bookingDate);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();

    // 3. Construct the Custom Receipt ID
    // Format: BO - [MM][DD][YY] - [CODE] - [ID]
    // Note: Using 26 for year as per your example '031026'
    const shortYear = String(year).slice(-2);
    const customReceiptId = `BO-M-${month}${day}${shortYear}-${paymentCode}-${booking.id}`;

    const subtotalBeforeTax = Number(booking.grandTotal) - Number(booking.tax);
    const discountInPercent =
      booking.grandTotal > 0
        ? (Number(booking.discount) / booking.grandTotal) * 100
        : 0;

    const totalPaid =
      Number(booking.cashAmount || 0) +
      Number(booking.onlineAmount || 0) +
      Number(booking.posAmount || 0);

    const payload: any = {
      totalExcTax: Number(subtotalBeforeTax),
      customerName: booking.customerName,
      cashierName: booking.cashierName,
      services: booking.cart,
      reason: booking.reason,
      subtotal: Number(subtotalBeforeTax),
      praTax: Number(booking.tax),
      totalPrice: Number(booking.grandTotal),
      GrandTotal: Number(booking.grandTotal),
      discountInPercentage: discountInPercent,
      discountInAmount: Number(booking.discount),
      amountPaid: Number(totalPaid),
      pending: Number(booking.grandTotal) - totalPaid, // Corrected logic
      customReceiptId,
      date: booking.bookingDate,
    };

    printStaffReceipts(payload);
  };

  return (
    <Layout>
      <div className="leavesIndex">
        <div className="leavesIndex__header" style={{ paddingBottom: 0 }}>
          <div className="leavesIndex__header_left">
            <h4 className="attendance_overview_heading">Booking</h4>
          </div>
        </div>

        <div className="leavesIndex__header">
          <div className="leavesIndex__header_left">
            <div className="remaingLeavesCounter">
              <div className="remaingLeavesCounterEachAttendance">
                <div
                  className={`searchNameFilterParent ${
                    isFocused ? "searchNameFilterParentFocused" : ""
                  }`}
                  style={{ marginRight: "1vw" }}
                >
                  <FormControl variant="standard">
                    <OutlinedInput
                      onChange={handleChange}
                      value={searchTerm}
                      className="searchNameFilter"
                      placeholder="Name/ Email/ Phone no"
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      endAdornment={
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      }
                    />
                  </FormControl>
                </div>
              </div>
              <div
                className={`searchNameFilterParent ${
                  isFocused ? "searchNameFilterParentFocused" : ""
                }`}
                style={{ marginRight: "1vw" }}
              >
                <select
                  name="paymentMethod"
                  id="leave_type"
                  className="searchNameFilter"
                  onChange={handleChangePaymentMethod}
                  value={paymentMethod}
                  style={{ padding: "5px" }}
                >
                  <option value="" disabled style={{ display: "none" }}>
                    Search By Payment Method
                  </option>
                  <option value="">All</option>
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                  <option value="both">Online & Cash</option>
                </select>
              </div>
              <div className="singleInputFieldFilters">
                <FormControl variant="standard">
                  <OutlinedInput
                    className="searchNameFilter"
                    placeholder="Select Date"
                    onClick={openModalDate}
                    value={selectedDate || ""}
                    readOnly
                  />
                </FormControl>
              </div>
              <div
                className="remaingLeavesCounterEachInventoryButton"
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                <button
                  onClick={handleDownloadPdf}
                  className="downloadPDFButton downloadPDFButton2"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
        <Card className="myLeaves__card" variant="outlined">
          <div>
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <TableContainer>
                <Table
                  stickyHeader
                  aria-label="sticky table"
                  id="your-table-id"
                >
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          style={{ textAlign: "center" }}
                        >
                          {column.sortable ? (
                            <TableSortLabel
                              active={sortBy === column.id}
                              direction={
                                sortBy === column.id ? sortOrder : "asc"
                              }
                              onClick={() => handleSort(column.id)}
                            >
                              {column.label}
                            </TableSortLabel>
                          ) : (
                            column.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedItems.map((row, index) => {
                      const serialNumber = indexOfFirstItem + index + 1;
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={serialNumber}
                        >
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {serialNumber}
                          </TableCell>
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            #{row["id"]}
                          </TableCell>
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {capitalizeEachWord(row["name"])}
                          </TableCell>
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {row["paymentMethods"] &&
                            row["paymentMethods"].toString().trim() !== ""
                              ? Array.isArray(row["paymentMethods"])
                                ? row["paymentMethods"].join(", ")
                                : row["paymentMethods"]
                              : "-"}
                          </TableCell>
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {row["totalPrice"] != null
                              ? Math.round(Number(row["totalPrice"]))
                              : "-"}{" "}
                          </TableCell>

                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {row["grandTotal"] != null
                              ? Math.round(Number(row["grandTotal"]))
                              : "-"}
                          </TableCell>
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {row["discount"] != null
                              ? Math.round(Number(row["discount"]))
                              : "-"}
                          </TableCell>
                          <TableCell
                            style={{
                              textAlign: "center",
                              color: "red",
                            }}
                          >
                            {row["pendingAmount"] != null
                              ? Math.round(Number(row["pendingAmount"]))
                              : "-"}
                          </TableCell>
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {row["bookingDate"]}
                          </TableCell>
                          <TableCell style={{ textAlign: "center" }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                              }}
                            >
                              {/* print */}
                              {/* <div>
                                <button
                                  style={{
                                    border: "none",
                                    color: "#0066ff",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handlePrintService(row["id"])}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="26"
                                    height="26"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M6 9V3h12v6h2a2 2 0 0 1 2 2v6h-4v4H6v-4H2v-6a2 2 0 0 1 2-2h2zm2-4v4h8V5H8zm8 14v-4H8v4h8zm4-8H4v4h2v-2h12v2h2v-4z" />
                                  </svg>
                                </button>
                              </div> */}

                              {/* print */}
                              <div style={{ margin: "0px 5px" }}>
                                <button
                                  style={{
                                    border: "none",
                                    color: "#af0000",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                    padding: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  onClick={() => handlePrintReceipt(row["id"])}
                                  title="Print Receipt"
                                >
                                  <PrintIcon fontSize="small" />
                                </button>
                              </div>
                              {/* view */}
                              <div style={{ margin: "0px 5px" }}>
                                <button
                                  style={{
                                    border: "none",
                                    color: "#0066ff",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleServiceView(row["id"])}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="26"
                                    height="26"
                                    viewBox="0 0 26 26"
                                  >
                                    <g data-name="Group 295">
                                      <path
                                        data-name="visibility_FILL0_wght300_GRAD0_opsz24 (1)"
                                        d="M13.003 16.95a4.236 4.236 0 0 0 3.115-1.284 4.248 4.248 0 0 0 1.281-3.117 4.236 4.236 0 0 0-1.284-3.115 4.248 4.248 0 0 0-3.117-1.281 4.236 4.236 0 0 0-3.115 1.284 4.248 4.248 0 0 0-1.281 3.117 4.236 4.236 0 0 0 1.284 3.115 4.248 4.248 0 0 0 3.117 1.281zm0-1.485a2.809 2.809 0 0 1-2.063-.85 2.809 2.809 0 0 1-.85-2.063 2.809 2.809 0 0 1 .85-2.063 2.809 2.809 0 0 1 2.063-.85 2.809 2.809 0 0 1 2.063.85 2.809 2.809 0 0 1 .85 2.063 2.809 2.809 0 0 1-.85 2.063 2.809 2.809 0 0 1-2.065.85zm0 4.639a11.281 11.281 0 0 1-6.231-1.817 13.565 13.565 0 0 1-4.509-4.782 1.9 1.9 0 0 1-.2-.468 1.892 1.892 0 0 1-.062-.484 1.884 1.884 0 0 1 .062-.484 1.9 1.9 0 0 1 .2-.468 13.564 13.564 0 0 1 4.509-4.782 11.281 11.281 0 0 1 6.229-1.82 11.281 11.281 0 0 1 6.231 1.818 13.564 13.564 0 0 1 4.514 4.782 1.9 1.9 0 0 1 .2.468 1.893 1.893 0 0 1 .062.484 1.885 1.885 0 0 1-.062.484 1.9 1.9 0 0 1-.2.468 13.565 13.565 0 0 1-4.509 4.782 11.281 11.281 0 0 1-6.236 1.814zm-.002-7.552zm0 5.934a10.3 10.3 0 0 0 5.6-1.6 10.543 10.543 0 0 0 3.9-4.329 10.544 10.544 0 0 0-3.9-4.329 10.3 10.3 0 0 0-5.6-1.6 10.3 10.3 0 0 0-5.6 1.6 10.544 10.544 0 0 0-3.9 4.329 10.543 10.543 0 0 0 3.9 4.329 10.3 10.3 0 0 0 5.6 1.6z"
                                      />
                                      <path
                                        data-name="Rectangle 293"
                                        fill="none"
                                        d="M0 0h26v26H0z"
                                      />
                                    </g>
                                  </svg>
                                </button>
                              </div>

                              {/* delete */}

                              <div>
                                <button
                                  style={{
                                    border: "none",
                                    color: "#0066ff",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleDeleteService(row["id"])}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 26 26"
                                  >
                                    <g data-name="Group 302">
                                      <path
                                        data-name="delete_FILL0_wght300_GRAD0_opsz24 (1)"
                                        d="M6.658 24a2.267 2.267 0 0 1-1.663-.692 2.268 2.268 0 0 1-.692-1.663V5.107h-.326a.945.945 0 0 1-.7-.281.946.946 0 0 1-.281-.7.944.944 0 0 1 .281-.7.946.946 0 0 1 .7-.281h4.886a1.111 1.111 0 0 1 .337-.816 1.111 1.111 0 0 1 .816-.337h5.513a1.111 1.111 0 0 1 .816.337 1.111 1.111 0 0 1 .337.816h4.886a.945.945 0 0 1 .7.281.947.947 0 0 1 .281.7.945.945 0 0 1-.281.7.946.946 0 0 1-.7.281h-.326v16.538a2.268 2.268 0 0 1-.692 1.663 2.267 2.267 0 0 1-1.663.692zM19.287 5.107H6.257v16.538a.39.39 0 0 0 .113.288.39.39 0 0 0 .288.113h12.228a.39.39 0 0 0 .288-.113.39.39 0 0 0 .113-.288zm-8.92 14.333a.944.944 0 0 0 .7-.281.946.946 0 0 0 .281-.7V8.687a.945.945 0 0 0-.281-.7.946.946 0 0 0-.7-.281.945.945 0 0 0-.7.281.946.946 0 0 0-.281.7v9.772a.946.946 0 0 0 .281.7.946.946 0 0 0 .7.281zm4.811 0a.945.945 0 0 0 .7-.281.946.946 0 0 0 .281-.7V8.687a.945.945 0 0 0-.281-.7.946.946 0 0 0-.7-.281.945.945 0 0 0-.7.281.946.946 0 0 0-.281.7v9.772a.945.945 0 0 0 .281.7.946.946 0 0 0 .7.281zM6.257 5.107z"
                                      />
                                      <path
                                        data-name="Rectangle 292"
                                        fill="none"
                                        d="M0 0h26v26H0z"
                                      />
                                    </g>
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100, 250]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </div>
        </Card>
        <DatePickerModal isOpen={isModalOpenDate} onClose={closeModalDate}>
          <div className="modal__popup">
            <div className="date-filter-container">
              <div
                className="date-pickers"
                style={{ display: "flex", gap: "16px" }}
              >
                <DateRangePicker
                  ranges={[selectionRange]}
                  staticRanges={staticRanges}
                  onChange={handleSelect}
                />
              </div>
              <div
                className="action-buttons"
                style={{ marginTop: "16px", textAlign: "right" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApply}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={closeModalDate}
                  style={{ marginLeft: "8px" }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </DatePickerModal>
        <RejectionModal isOpen={isModalOpen} onClose={closeModal}>
          <div className="modal__popup">
            <div className="Portal-Login-Form">
              <div
                className="ValidationModal"
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span className="approvalText">
                  Are you sure that you want to delete that booking?
                </span>

                <div className="CloseRejectionModalButton">
                  <button
                    type="button"
                    className="CloseRejectionModal"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rejectButtonValidationModal"
                    onClick={closeModalWithDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </RejectionModal>
      </div>
    </Layout>
  );
}
